import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle,
  XCircle,
  Warning,
  ArrowClockwise,
  CreditCard,
  EnvelopeSimple,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Realtidshälsokoll av externa integrationer (Stripe, Resend).
 *
 * Poängen: du ska ALDRIG behöva vara osäker på om en nyckel funkar i
 * preview vs produktion. Klicka en knapp — se svaret. Färgkodat.
 */
export default function IntegrationsHealth() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingResend, setTestingResend] = useState(false);
  const [testingStripe, setTestingStripe] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/integrations/status");
      setStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte hämta integrationsstatus");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const testResend = async () => {
    try {
      setTestingResend(true);
      const r = await api.post("/integrations/resend/test-email", {});
      toast.success(`Testmail skickat till ${r.data.to} (id ${(r.data.email_id || "").slice(0, 8)}…)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte skicka testmail");
    } finally {
      setTestingResend(false);
    }
  };

  const testStripe = async () => {
    try {
      setTestingStripe(true);
      const r = await api.post("/integrations/stripe/test", {});
      toast.success(`Stripe OK: ${r.data.account} (${r.data.mode})`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stripe-testet misslyckades");
    } finally {
      setTestingStripe(false);
    }
  };

  return (
    <section className="tk-card p-6 space-y-5" data-testid="integrations-health">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
            <CheckCircle size={14} /> Anslutningar &amp; nycklar
          </div>
          <h2 className="font-heading text-2xl mt-1">Är alla nycklar friska?</h2>
          <p className="text-sm text-text-muted mt-1">
            Klicka på "Testa" för att prova nyckeln på riktigt i den{" "}
            <strong>
              {status?.environment === "production" ? "produktionsmiljön" : "preview-miljön"}
            </strong>{" "}
            du är inne i just nu. Så du slipper vara nervös.
          </p>
        </div>
        <button
          data-testid="integrations-refresh"
          onClick={load}
          disabled={loading}
          className="tk-btn-outline !py-2 !px-3 inline-flex items-center gap-2 text-sm"
        >
          <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} />
          Uppdatera
        </button>
      </div>

      {loading && !status ? (
        <div className="text-sm text-text-muted p-4">Kollar …</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatusCard
            testId="stripe-card"
            icon={<CreditCard size={22} />}
            title="Stripe"
            subtitle="Prenumerationer &amp; betalningar"
            state={status?.stripe}
            actionLabel={testingStripe ? "Testar…" : "Testa Stripe"}
            actionIcon={<CheckCircle size={14} weight="bold" />}
            onAction={testStripe}
            actionBusy={testingStripe}
            actionTestId="test-stripe-btn"
          />
          <StatusCard
            testId="resend-card"
            icon={<EnvelopeSimple size={22} />}
            title="Resend"
            subtitle="E-postutskick"
            state={status?.resend}
            actionLabel={testingResend ? "Skickar…" : `Skicka test till ${user?.email || "mig"}`}
            actionIcon={<PaperPlaneTilt size={14} weight="bold" />}
            onAction={testResend}
            actionBusy={testingResend}
            actionTestId="test-resend-btn"
          />
        </div>
      )}

      <div className="text-[11px] text-text-muted bg-[var(--brand-sand)]/40 border border-app rounded-lg px-3 py-2">
        <strong>Tips:</strong> Om ett kort är rött behöver du uppdatera nyckeln i{" "}
        <em>Deploy-panelen &rarr; Secrets</em> och deploya om. I preview räcker det att
        uppdatera <code>backend/.env</code> och be assistenten starta om backend.
      </div>
    </section>
  );
}

function StatusCard({
  testId, icon, title, subtitle, state,
  actionLabel, actionIcon, onAction, actionBusy, actionTestId,
}) {
  const healthy = state?.healthy;
  const configured = state?.configured;
  const color = healthy
    ? "border-emerald-300 bg-emerald-50/60"
    : configured
    ? "border-rose-300 bg-rose-50/60"
    : "border-amber-300 bg-amber-50/60";
  const StatusIcon = healthy ? CheckCircle : configured ? XCircle : Warning;
  const iconColor = healthy
    ? "text-emerald-600"
    : configured
    ? "text-rose-600"
    : "text-amber-600";

  return (
    <div
      data-testid={testId}
      className={`rounded-xl border-2 ${color} p-4 space-y-3`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-white border border-app flex items-center justify-center text-[var(--brand-forest)]">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-lg leading-tight">{title}</div>
          <div className="text-xs text-text-muted">{subtitle}</div>
        </div>
        <StatusIcon size={22} weight="fill" className={iconColor} />
      </div>

      <div className="text-xs space-y-1">
        <div>
          <span className="text-text-muted">Status: </span>
          <strong data-testid={`${testId}-status`}>
            {healthy ? "Fungerar" : configured ? "Fel" : "Inte konfigurerad"}
          </strong>
        </div>
        {state?.detail && (
          <div className="text-text-muted break-words" data-testid={`${testId}-detail`}>
            {state.detail}
          </div>
        )}
        {state?.account && (
          <div>
            <span className="text-text-muted">Konto: </span>
            <span data-testid={`${testId}-account`}>{state.account}</span>
          </div>
        )}
        {state?.mode && (
          <div>
            <span className="text-text-muted">Läge: </span>
            <span
              data-testid={`${testId}-mode`}
              className={
                state.mode === "live"
                  ? "text-emerald-700 font-semibold"
                  : "text-amber-700"
              }
            >
              {state.mode === "live" ? "LIVE" : state.mode.toUpperCase()}
            </span>
          </div>
        )}
        {state?.key_hint && (
          <div>
            <span className="text-text-muted">Nyckel: </span>
            <code className="text-[10px]" data-testid={`${testId}-key-hint`}>
              {state.key_hint}
            </code>
          </div>
        )}
      </div>

      <button
        data-testid={actionTestId}
        onClick={onAction}
        disabled={actionBusy || !configured}
        className="tk-btn-primary !py-2 !px-4 inline-flex items-center gap-2 text-sm w-full justify-center disabled:opacity-60"
      >
        {actionIcon}
        {actionLabel}
      </button>
    </div>
  );
}
